import React, { useState, useEffect, useRef } from 'react';
import { Film, Download } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { CollapsibleSection } from '../../../components/ui/CollapsibleSection';

const PRESETS = [
    { label: '4 h', hours: 4 },
    { label: '6 h', hours: 6 },
    { label: '12 h', hours: 12 },
    { label: '24 h', hours: 24 },
];

// datetime-local value in local time
const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ExportSettings = ({ cameras = [], token, showToast, isOpen, onToggle }) => {
    const [cameraId, setCameraId] = useState('');
    const [start, setStart] = useState(() => toLocalInput(new Date(Date.now() - 6 * 3600 * 1000)));
    const [end, setEnd] = useState(() => toLocalInput(new Date()));
    const [job, setJob] = useState(null); // { id, status, filename, clips, error }
    const timer = useRef(null);

    useEffect(() => () => clearInterval(timer.current), []);

    const applyPreset = (hours) => {
        const now = new Date();
        setEnd(toLocalInput(now));
        setStart(toLocalInput(new Date(now.getTime() - hours * 3600 * 1000)));
    };

    const poll = (id) => {
        clearInterval(timer.current);
        timer.current = setInterval(async () => {
            try {
                const res = await fetch(`/api/exports/${id}`, { headers: { Authorization: 'Bearer ' + token } });
                if (!res.ok) throw new Error();
                const data = await res.json();
                setJob((j) => ({ ...j, ...data }));
                if (data.status !== 'running') clearInterval(timer.current);
            } catch {
                clearInterval(timer.current);
                setJob((j) => ({ ...j, status: 'error', error: 'Lost connection' }));
            }
        }, 2000);
    };

    const startExport = async () => {
        if (!cameraId) return showToast('Choisissez une caméra', 'error');
        try {
            const res = await fetch('/api/exports', {
                method: 'POST',
                headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    camera_id: Number(cameraId),
                    start: new Date(start).toISOString(),
                    end: new Date(end).toISOString(),
                }),
            });
            const data = await res.json();
            if (!res.ok) return showToast(data.detail || 'Export impossible', 'error');
            setJob({ id: data.job_id, status: 'running', clips: data.clips });
            poll(data.job_id);
        } catch {
            showToast('Export impossible', 'error');
        }
    };

    const inputCls = 'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm';

    return (
        <CollapsibleSection
            id="export"
            title="Export vidéo"
            description="Sortir un fichier MP4 d'une caméra sur une plage horaire choisie (4 h, 6 h, 24 h...)."
            icon={<Film className="w-6 h-6" />}
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <div className="space-y-4 pt-2">
                <div>
                    <label className="text-sm font-medium block mb-1">Caméra</label>
                    <select className={inputCls} value={cameraId} onChange={(e) => setCameraId(e.target.value)}>
                        <option value="">— Choisir —</option>
                        {cameras.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">Dernières :</span>
                    {PRESETS.map((p) => (
                        <button
                            key={p.hours}
                            type="button"
                            onClick={() => applyPreset(p.hours)}
                            className="px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium"
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-medium block mb-1">Début</label>
                        <input type="datetime-local" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-sm font-medium block mb-1">Fin</label>
                        <input type="datetime-local" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} />
                    </div>
                </div>

                <p className="text-xs text-muted-foreground">
                    Format MP4. Les clips enregistrés sur la plage sont assemblés en un seul fichier (clips entiers, sans trou comblé). Max 48 h.
                </p>

                <Button onClick={startExport} disabled={job?.status === 'running'}>
                    {job?.status === 'running' ? 'Préparation...' : 'Créer le fichier'}
                </Button>

                {job?.status === 'running' && (
                    <p className="text-sm text-muted-foreground">Assemblage de {job.clips} clips en cours...</p>
                )}
                {job?.status === 'error' && (
                    <p className="text-sm text-red-500 break-words">Échec : {job.error}</p>
                )}
                {job?.status === 'done' && (
                    <a
                        href={`/api/exports/${job.id}/download`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-bold text-sm"
                    >
                        <Download className="w-4 h-4" /> Télécharger {job.filename}
                    </a>
                )}
            </div>
        </CollapsibleSection>
    );
};
